/**
 * Formatters for `GET /ai/ambient-scribes/{id}/transcript-lines/`.
 *
 * These describe the endpoint's data shape, not any one feature: the backend's
 * `build_transcript_lines` is the single source of transcript numbering, and
 * both Follow Ups (evidence highlighting) and the Ambient Scribe details dialog
 * (the Transcript tab) render its rows. They live here rather than in either
 * feature so the two surfaces cannot drift — in particular so `SPK0` can never
 * reach a screen from one of them.
 */

/**
 * Offset into the recording, formatted the way the generation prompt formats
 * it, so a timestamp in the transcript reader matches the one printed in the
 * prompt a Suno admin is reading beside it. That means minutes are NOT capped
 * at 59 and NOT zero-padded: 3700s is "61:40", never "1:01:40".
 */
export function formatAtSeconds(seconds: number | null): string {
  if (seconds == null || seconds < 0) return "";
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

/**
 * `evidence_speaker` and `TranscriptLine.speaker` are diarisation placeholders
 * — `"SPK0"`…`"SPK99"`, or `"SPK?"` when diarisation produced nothing. They are
 * never a clinical role, so we must not print "The patient" or a clinician's
 * name from them. `SPK0` becomes "Speaker 1" (1-based reads naturally); an
 * unknown speaker returns null so callers show the timestamp alone.
 */
export function speakerLabel(
  speaker: string | null | undefined
): string | null {
  if (!speaker) return null;
  const match = /^SPK(\d+)$/.exec(speaker.trim());
  if (!match) return null;
  return `Speaker ${Number(match[1]) + 1}`;
}
